// Types, constants and pure transformation logic for pricing config.
// Intentionally has NO @tanstack/react-query dependency so it's safe
// to include in the main @repo/ui barrel export.

export enum SubscriptionPeriod {
  Yearly = "yearly",
  Monthly = "monthly",
}

export const CurrencySymbols = new Map([
  ["USD", "$"],
  ["CNY", "¥"],
  ["MXN", "Mex$"],
  ["GBP", "£"],
  ["AUD", "AU$"],
  ["CAD", "CA$"],
  ["THB", "฿"],
  ["EUR", "€"],
]);

export type PriceConfigResponseData = Array<{
  subscriptionPeriod: SubscriptionPeriod;
  tier: SubscriptionTier;
  price: number;
  currencyCode: string;
  priceIncludesTax: boolean;
}>;

export type TierPriceConfig = {
  monthly: number;
  yearly: number;
  savingsPercent: number;
  priceIncludesTax: boolean;
};

export enum SubscriptionTier {
  Pro = "pro",
  Lite = "lite",
  Individual = "individual",
  Studio = "studio",
}

export type PriceConfig = {
  tiers: Partial<Record<SubscriptionTier, TierPriceConfig>>;
  currencySymbol: string;
  currencyCode: string;
  availablePeriods: SubscriptionPeriod[];
};

export const priceConfigQueryKey = ["prices"];

export const transformPriceConfig = (data: PriceConfigResponseData): PriceConfig => {
  const tierNames = [...new Set(data.map((c) => c.tier))];
  const tiers: Record<string, TierPriceConfig> = {};

  for (const tier of tierNames) {
    const tierData = data.filter((c) => c.tier === tier);
    const monthlyConfig = tierData.find((c) => c.subscriptionPeriod === SubscriptionPeriod.Monthly);
    const yearlyConfig = tierData.find((c) => c.subscriptionPeriod === SubscriptionPeriod.Yearly);

    const monthly = (monthlyConfig?.price ?? 0) / 100;
    const yearly = (yearlyConfig?.price ?? 0) / 100;
    const savingsPercent = Math.round((1 - yearly / (monthly * 12)) * 100);

    const priceIncludesTax =
      monthlyConfig?.priceIncludesTax ?? yearlyConfig?.priceIncludesTax ?? false;

    tiers[tier] = { monthly, yearly, savingsPercent, priceIncludesTax };
  }

  const anyCurrencyCode = data[0]?.currencyCode ?? "USD";

  return {
    tiers,
    currencySymbol: CurrencySymbols.get(anyCurrencyCode) ?? "$",
    currencyCode: anyCurrencyCode,
    availablePeriods: [SubscriptionPeriod.Monthly, SubscriptionPeriod.Yearly],
  };
};
